import { WebMCPToolDefinition, WebModelContextAPI, WebMCPTelemetryLog } from '../types/webmcp';

type TelemetryListener = (log: WebMCPTelemetryLog) => void;

class ModelContextPolyfill implements WebModelContextAPI {
  private tools: Map<string, WebMCPToolDefinition> = new Map();
  private telemetryListeners: Set<TelemetryListener> = new Set();
  private eventListeners: Map<string, Set<(...args: any[]) => void>> = new Map();

  constructor() {
    if (typeof window !== 'undefined') {
      console.log('[WebMCP] Initializing Web Model Context Protocol (v1.0-draft) for AutoFix.run');
    }
  }

  public registerTool(tool: WebMCPToolDefinition): void {
    if (!tool.name) throw new Error('[WebMCP] Tool name is required');
    this.tools.set(tool.name, tool);
    this.emitEvent('toolRegistered', tool);
    console.log(`[WebMCP] ✅ Registered tool: ${tool.name}`);
  }

  public unregisterTool(toolName: string): void {
    if (this.tools.delete(toolName)) {
      this.emitEvent('toolUnregistered', toolName);
    }
  }

  public getTools(): WebMCPToolDefinition[] {
    return Array.from(this.tools.values());
  }

  public getTool(toolName: string): WebMCPToolDefinition | undefined {
    return this.tools.get(toolName);
  }

  public async executeTool(
    name: string,
    input: any,
    caller: WebMCPTelemetryLog['caller'] = 'chrome-modelcontext'
  ): Promise<any> {
    const tool = this.tools.get(name);
    const startTime = performance.now();
    const logId = 'log_' + Math.random().toString(36).substring(2, 9);

    if (!tool) {
      const errorMsg = `[WebMCP] Tool '${name}' not found`;
      this.emitTelemetry({
        id: logId, timestamp: Date.now(), toolName: name, caller,
        status: 'failed', input, error: errorMsg, executionTimeMs: 0,
      });
      throw new Error(errorMsg);
    }

    this.emitTelemetry({
      id: logId, timestamp: Date.now(), toolName: name, caller,
      status: 'executing', input,
    });

    try {
      const result = await tool.execute(input);
      const executionTimeMs = Math.round(performance.now() - startTime);
      this.emitTelemetry({
        id: logId, timestamp: Date.now(), toolName: name, caller,
        status: 'completed', input, output: result, executionTimeMs,
      });
      return result;
    } catch (err: any) {
      const executionTimeMs = Math.round(performance.now() - startTime);
      this.emitTelemetry({
        id: logId, timestamp: Date.now(), toolName: name, caller,
        status: 'failed', input, error: err?.message || String(err), executionTimeMs,
      });
      throw err;
    }
  }

  public onTelemetry(listener: TelemetryListener): () => void {
    this.telemetryListeners.add(listener);
    return () => this.telemetryListeners.delete(listener);
  }

  private emitTelemetry(log: WebMCPTelemetryLog): void {
    this.telemetryListeners.forEach((listener) => {
      try { listener(log); } catch (err) { console.error('[WebMCP] Telemetry error:', err); }
    });
  }

  public addEventListener(event: string, callback: (...args: any[]) => void): void {
    if (!this.eventListeners.has(event)) this.eventListeners.set(event, new Set());
    this.eventListeners.get(event)!.add(callback);
  }

  public removeEventListener(event: string, callback: (...args: any[]) => void): void {
    this.eventListeners.get(event)?.delete(callback);
  }

  private emitEvent(event: string, ...args: any[]): void {
    this.eventListeners.get(event)?.forEach((cb) => {
      try { cb(...args); } catch (e) { console.error(`[WebMCP] Event '${event}' error:`, e); }
    });
  }
}

export const modelContextInstance = new ModelContextPolyfill();

export function initializeWebMCPPolyfill(): ModelContextPolyfill {
  if (typeof window !== 'undefined') {
    if (!window.navigator.modelContext) {
      (window.navigator as any).modelContext = modelContextInstance;
      console.log('[WebMCP] ✅ Installed navigator.modelContext polyfill for AutoFix.run');
    } else {
      console.log('[WebMCP] Native navigator.modelContext detected');
    }
  }
  return modelContextInstance;
}
